import { useEffect, useState } from 'react';
import type { CloseReason, ParticipantView, VoiceChoice } from '@voxa/shared';
import { useCall } from '../lib/useCall.ts';
import type { TranslatorFailure } from '../lib/translator/failures.ts';
import { AudioSink } from '../components/AudioSink.tsx';
import { StatusPills } from '../components/StatusPills.tsx';
import { Subtitles } from '../components/Subtitles.tsx';
import { Rating } from '../components/Rating.tsx';
import { stopStream } from '../lib/devices.ts';
import { useT, type Dictionary } from '../i18n/index.tsx';
import { capabilitiesNow } from '../lib/capabilities.ts';
import { costOf, usd } from '../lib/cost.ts';

export function Call({
  participant,
  micStream,
  onFinished,
}: {
  participant: ParticipantView;
  micStream: MediaStream;
  onFinished: () => void;
}) {
  const t = useT();
  const {
    view,
    originalStream,
    translatedStream,
    secondsLeft,
    toggleMic,
    restartTranslator,
    wakeTranslator,
    leave,
  } = useCall({ participant, micStream });

  const [translationVolume, setTranslationVolume] = useState(1);
  const [originalVolume, setOriginalVolume] = useState(0.15);
  // Section 3: the peer's untranslated voice is off unless asked for.
  const [hearOriginal, setHearOriginal] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(secondsLeft);

  // Task 6. The creator chose the voices and pays for the call, so the voice
  // pills, the voice-over delay and the running cost are theirs to see. The
  // guest's screen is unchanged: they were never told which voice they got.
  const creator = participant.seat === 0;
  const secondVoice =
    participant.voice?.provider === 'elevenlabs' ||
    participant.peerVoice?.provider === 'elevenlabs';
  const pricing = capabilitiesNow()?.pricing;
  const cost = creator && pricing ? costOf([view.usage.me, view.usage.peer], pricing) : null;
  const voiceLabel = (v: VoiceChoice | undefined, fellBack = false) =>
    v?.provider === 'elevenlabs' && !fellBack
      ? `ElevenLabs${v.name ? ` · ${v.name}` : ''}`
      : v?.provider === 'elevenlabs'
        ? t.call.voiceFellBack
        : 'OpenAI';

  useEffect(() => setRemaining(secondsLeft), [secondsLeft]);

  useEffect(() => {
    if (view.callEndsAt === null) return;
    const endsAt = view.callEndsAt;
    const timer = setInterval(
      () => setRemaining(Math.max(0, Math.round((endsAt - Date.now()) / 1000))),
      1000,
    );
    return () => clearInterval(timer);
  }, [view.callEndsAt]);

  // Section 13: the microphone must stop when the call does, not when the tab
  // eventually gets collected.
  useEffect(() => {
    if (view.phase !== 'ended') return;
    stopStream(micStream);
  }, [micStream, view.phase]);

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (view.phase === 'live') e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [view.phase]);

  if (view.phase === 'ended') {
    return (
      <>
        <div className="card">
          <h1>{t.call.endedTitle}</h1>
          <p className="lede">
            {closedReason(t, view.closeReason, view.signalLost)} {t.call.endedNewRoom}
          </p>
          <div className="actions">
            <button type="button" className="primary" onClick={onFinished}>
              {t.call.newRoom}
            </button>
          </div>
        </div>
        <div className="card">
          <h2>{t.call.rateTitle}</h2>
          <Rating />
        </div>
      </>
    );
  }

  return (
    <div className="card">
      <h1>
        {t.call.title}{' '}
        {remaining !== null && (
          <span className="timer" style={{ fontSize: 16, color: 'var(--muted)' }}>
            {t.call.timeLeft(formatTime(remaining))}
          </span>
        )}
      </h1>

      <StatusPills
        signaling={view.signaling}
        peer={view.peer}
        translator={view.translator}
        peerTranslator={view.peerTranslator}
        relayed={view.relayed}
      />

      {creator && secondVoice && (
        <div className="statuses">
          <span className={`pill${view.voiceFellBack ? ' warn' : ''}`}>
            {t.call.iHear(voiceLabel(participant.voice, view.voiceFellBack))}
          </span>
          <span className="pill">{t.call.peerHears(voiceLabel(participant.peerVoice))}</span>
        </div>
      )}

      {!view.peerPresent && view.phase !== 'live' && (
        <div className="notice">{t.call.waitingPeer}</div>
      )}

      {autoplayBlocked && (
        <div className="notice warn">
          {t.call.autoplayBlocked}{' '}
          <button type="button" onClick={() => setAutoplayBlocked(false)}>
            {t.call.enableSound}
          </button>
        </div>
      )}

      {view.lagging && <div className="notice warn">{t.call.lagging}</div>}

      {view.translator === 'failed' && (
        <div className="notice bad">
          {t.call.translatorStopped(failureText(t, view.translatorFailure))}
        </div>
      )}

      {view.translator === 'asleep' && (
        <div className="notice">
          {t.call.asleep}{' '}
          {/* For when the voice check cannot hear: a blocked audio context,
              a very soft speaker. Waking by hand costs the same as by voice. */}
          <button type="button" onClick={wakeTranslator}>
            {t.call.wake}
          </button>
        </div>
      )}

      {view.error && <div className="notice bad">{view.error}</div>}

      <Subtitles
        me={view.subtitles.me}
        peer={view.subtitles.peer}
        myLanguage={participant.myLanguage}
        peerLanguage={participant.peerLanguage}
      />

      <div className="metrics">
        <Metric label={t.call.metricConnect} value={ms(view.metrics.connectMs)} />
        <Metric label={t.call.metricFirst} value={ms(view.metrics.firstTranslationMs)} />
        <Metric label={t.call.metricLag} value={ms(view.metrics.estimatedLagMs)} />
        <Metric label={t.call.metricRtt} value={ms(view.metrics.rttMs)} />
        {creator && participant.voice?.provider === 'elevenlabs' && !view.voiceFellBack && (
          <Metric label={t.call.metricVoiceDelay} value={ms(view.voiceDelayMs)} />
        )}
        {creator && participant.voice?.provider === 'elevenlabs' && !view.voiceFellBack && (
          <Metric label={t.call.metricVoiceLead} value={leadText(t, view.voiceLead.avgMs)} />
        )}
      </div>
      {cost && (
        <div className="cost">
          <span className="cost-label">{t.call.metricCost}</span>
          <b>{usd(cost.totalUsd)}</b>
          <span className="cost-detail">
            {t.call.costBreakdown(usd(cost.openaiUsd), usd(cost.voiceUsd), cost.voiceCredits)}.{' '}
            {t.call.costNote}
          </span>
        </div>
      )}

      <div className="field" style={{ marginTop: 20 }}>
        <div className="slider">
          <span>{t.call.translationVolume}</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            aria-label={t.call.translationVolume}
            value={translationVolume}
            onChange={(e) => setTranslationVolume(Number(e.target.value))}
          />
        </div>
      </div>

      <div className="field">
        <div className="slider">
          <label style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={hearOriginal}
              onChange={(e) => setHearOriginal(e.target.checked)}
            />
            {t.call.hearOriginal}
          </label>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            aria-label={t.call.hearOriginal}
            value={originalVolume}
            disabled={!hearOriginal}
            onChange={(e) => setOriginalVolume(Number(e.target.value))}
          />
        </div>
      </div>

      <div className="actions">
        <button type="button" onClick={toggleMic}>
          {view.micMuted ? t.call.unmuteMic : t.call.muteMic}
        </button>
        <button type="button" onClick={restartTranslator}>
          {t.call.restart}
        </button>
        <button
          type="button"
          className="danger"
          onClick={() => {
            void leave();
          }}
        >
          {t.call.leave}
        </button>
        {view.peerMicMuted && <span className="pill warn">{t.call.peerMuted}</span>}
      </div>

      {/* The translated audio is what the listener is here for. */}
      <AudioSink
        stream={translatedStream}
        volume={translationVolume}
        onBlocked={setAutoplayBlocked}
      />
      {/* The original voice stays attached but silent until explicitly enabled. */}
      <AudioSink
        stream={originalStream}
        volume={hearOriginal ? originalVolume : 0}
        muted={!hearOriginal}
      />
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <b>{value}</b>
      <span>{label}</span>
    </div>
  );
}

function failureText(t: Dictionary, failure: TranslatorFailure | null): string {
  if (!failure) return t.errors.unknown;
  if (failure.code === 'provider_refused') {
    return t.errors.provider_refused(failure.status ?? 0);
  }
  return t.errors[failure.code];
}

function closedReason(t: Dictionary, reason: CloseReason | null, signalLost: boolean): string {
  if (reason) return t.closed[reason];
  return signalLost ? t.closed.signalLost : t.closed.unknown;
}

const ms = (value: number | null) => (value === null ? '—' : `${value} ms`);

/** Positive: ElevenLabs was heard first. Tenths of a second, which is what an ear notices. */
function leadText(t: Dictionary, leadMs: number | null): string {
  if (leadMs === null) return '—';
  const seconds = (Math.abs(leadMs) / 1000).toFixed(1);
  return leadMs >= 0 ? t.call.voiceLeadEarlier(seconds) : t.call.voiceLeadLater(seconds);
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
